import React from 'react';
import { PageMeta } from '../PageMeta';
import { PageTitle } from '../../ui/PageTitle';
import { LeadParagraph } from '../../ui/LeadParagraph';
import { BodyParagraph } from '../../ui/BodyParagraph';
import { HighlightTag } from '../../ui/HighlightTag';
import { Quote } from '../../ui/Quote';
import { SectionHeading } from '../../ui/SectionHeading';
import { OG_IMAGE } from '../../constants/assets';
export function RequiredActions() {
  return <>
      <PageMeta title="Turning rules into a workflow brokers can clear" description="Required Actions had become the slowest part of a broker's day. I rebuilt how a policy resolves, not just how it looks, cutting broker effort by 60%." url="https://mintextract.design/work/required-actions" ogImage={OG_IMAGE} data-id="element-14" />
      <PageTitle data-id="element-15">Turning rules into a workflow brokers can clear</PageTitle>

      <div className="space-y-10 md:space-y-12" data-id="element-16">
        <LeadParagraph data-id="element-17">
          Required Actions had become the slowest part of a broker's day. I
          treated it as a rulebase problem, not a screen problem, and rebuilt
          how a policy resolves rather than how it looks.
        </LeadParagraph>

        <section className="space-y-4" data-id="element-18">
          <SectionHeading data-id="element-19">The system, and where it broke</SectionHeading>
          <div className="flex flex-col gap-4" data-id="element-20">
            <BodyParagraph data-id="element-21">
              A Required Action is the atomic unit brokers work in. Each one
              carries two things: WHAT the policy is, and HOW to satisfy it.
            </BodyParagraph>
            <BodyParagraph data-id="element-22">
              The feature existed to pre-empt MIRs, the Missing Information
              Required flags a lender raises when a submitted application is
              incomplete. Clear the actions up front, avoid the flag, and the
              deal moves to settlement faster with less back and forth. That was
              the point.
            </BodyParagraph>
            <BodyParagraph data-id="element-23">
              As the rulebase scaled it did the opposite. What began as tens of
              actions grew into the hundreds. Every new policy added a rule,
              every rule demanded a manual free-text comment to mitigate, and
              nothing governed the growth.
            </BodyParagraph>
            <BodyParagraph data-id="element-24">
              Median completion sat around 40 minutes. The tail was worse:
              frustrated or inexperienced brokers reported 2 to 5 hours.
            </BodyParagraph>
            <Quote data-id="element-25">
              There are so many questions. It took me five hours to complete (…)
              so many duplicates…
            </Quote>
            <BodyParagraph data-id="element-26">
              Run against usability heuristics, the dominant failure was not
              aesthetic. It was flexibility and efficiency of use: one
              resolution method, free text, forced on every rule whether or not
              the policy needed prose at all.{' '}
              <HighlightTag data-id="element-27">
                The cost was in the model, not the layout.
              </HighlightTag>
            </BodyParagraph>
          </div>
        </section>

        <section className="space-y-4" data-id="element-28">
          <SectionHeading data-id="element-29">The reframe</SectionHeading>
          <div className="space-y-4" data-id="element-30">
            <BodyParagraph data-id="element-31">
              I stopped designing answers and started designing the thing that
              produces them. Three moves, in order.
            </BodyParagraph>
            <ol className="list-decimal pl-5 space-y-6 text-[1.05rem] md:text-[clamp(1.05rem,1.5vw,1.333rem)] text-[#7A8290] leading-relaxed animate-fadeIn marker:text-[#7A8290]" data-id="element-32">
              <li data-id="element-33">
                <span className="block mb-1" data-id="element-34">Govern the rulebase</span>I audited
                the full set: 984 lender-specific variants across roughly 351
                unique actions, deleting or modifying 285. Then I set a new
                audit rhythm and raised the threshold for adding new actions, so
                the system could not quietly re-bloat. This fixed the growth
                mechanism, not just the current mess.
              </li>
              <li data-id="element-35">
                <span className="block mb-1" data-id="element-36">Type the resolution</span>I
                replaced the single free-text field with a small typology of
                mitigation methods, matched to what each rule actually requires:
                <ul className="list-disc pl-5 space-y-2 mt-3 mb-3 marker:text-[#7A8290]" data-id="element-37">
                  <li data-id="element-38">
                    One-click Yes/No for internal checks that never needed prose
                  </li>
                  <li data-id="element-39">
                    Automated lookups for verifiable facts, such as Equifax
                    inquiries
                  </li>
                  <li data-id="element-40">
                    Auto-populated fields for known artefacts, such as Exit
                    Strategies
                  </li>
                </ul>
                The reusable primitive became simple: a rule has a mitigation
                type.{' '}
                <HighlightTag data-id="element-41">
                  Most rules now resolve without a broker writing anything.
                  Solve the type once, and every future rule of that type
                  inherits the fix.
                </HighlightTag>
              </li>
            </ol>
            <div className="!mt-8 !mb-8 animate-fadeIn" data-id="element-42">
              <div className="rounded-lg overflow-hidden" style={{
              aspectRatio: '4/3'
            }} data-id="element-43">
                <img src="https://cdn.magicpatterns.com/uploads/7HHnB4xcVtHavMSLJY3H8p/One_click_mitigations.png" alt="One-click mitigations — a typed Yes/No resolution replacing free-text comments on a Foreign Tax Resident Information rule" className="w-full h-full object-cover" data-id="element-44" />
              </div>
            </div>
            <ol start={3} className="list-decimal pl-5 space-y-6 text-[1.05rem] md:text-[clamp(1.05rem,1.5vw,1.333rem)] text-[#7A8290] leading-relaxed animate-fadeIn marker:text-[#7A8290]" data-id="element-45">
              <li data-id="element-46">
                <span className="block mb-1" data-id="element-47">Align to workflow</span>
                Actions were grouped alphabetically, loosely following the
                platform's sequence, which matched no broker's mental model. I
                ran an open card sort to cluster them to how an application is
                actually worked, and to cut the number of groupings down to
                something legible. The information architecture followed the
                workflow, not the old policy taxonomy.
              </li>
            </ol>
          </div>
        </section>

        <section className="space-y-4" data-id="element-48">
          <SectionHeading data-id="element-49">
            The interface as an expression of the model
          </SectionHeading>
          <div className="space-y-4" data-id="element-50">
            <BodyParagraph data-id="element-51">
              Only once the model was right did the surface follow. Every
              concept ran through design office hours with engineering before it
              reached brokers, so feasibility and back-end integration were
              settled early rather than discovered late.
            </BodyParagraph>
            <BodyParagraph data-id="element-52">
              Smart formats surface the mitigation type inline. A split-screen
              layout and simplified navigation carry the work. Different
              scenarios call for different sets of actions, so filters and
              facets let a broker narrow a long list to the few that apply.
            </BodyParagraph>
            <div className="!mt-8 !mb-8 animate-fadeIn" data-id="element-53">
              <div className="rounded-lg overflow-hidden" style={{
              aspectRatio: '4/3'
            }} data-id="element-54">
                <img src="https://cdn.magicpatterns.com/uploads/fbfeUo9KxaoRfytvVVPCFT/Infrastructure_update.png" alt="Infrastructure update and new interface — split-screen layout, simplified navigation, and smart formats in the rebuilt Required Actions panel" className="w-full h-full object-cover" data-id="element-55" />
              </div>
            </div>
            <BodyParagraph data-id="element-56">
              The clearest single decision: a free-text comment field, answered
              from scratch every time, became a typed one-click mitigation. Same
              policy, resolved in a tap instead of a paragraph.
            </BodyParagraph>
          </div>
        </section>

        <section className="space-y-4" data-id="element-57">
          <SectionHeading data-id="element-58">Outcomes</SectionHeading>
          <div className="flex flex-col gap-4" data-id="element-59">
            <ul className="list-disc pl-5 space-y-2 text-[1.05rem] md:text-[clamp(1.05rem,1.5vw,1.333rem)] text-[#7A8290] leading-relaxed animate-fadeIn marker:text-[#7A8290]" data-id="element-60">
              <li data-id="element-61">
                20 to 30% reduction in median lender policy processing time per
                application
              </li>
              <li data-id="element-62">60% reduction in broker effort</li>
              <li data-id="element-63">13,000 minutes saved per month across the network</li>
            </ul>
            <Quote data-id="element-64">
              Remarkable improvement because it's not just the fluidity of the
              process from a literal time-saving perspective; it's now short
              enough to enable the mental focus to do it all in one go.
              Previously, I'd need a breather and do something else.
            </Quote>
          </div>
        </section>

        <section className="space-y-4" data-id="element-65">
          <SectionHeading data-id="element-66">Why it held</SectionHeading>
          <div className="space-y-4" data-id="element-67">
            <BodyParagraph data-id="element-68">
              The win was not a cleaner screen. It was a governed rulebase and a
              typed resolution model that resist the exact decay that broke the
              original.{' '}
              <HighlightTag data-id="element-69">The interface is downstream of both.</HighlightTag>
            </BodyParagraph>
          </div>
        </section>
      </div>
    </>;
}